import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { FormSearchableSelect } from '../form/FormSearchableSelect'
import { getCountries } from '@/api/countriesAPI'

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../primitives/Dialog'
import { Button } from '../primitives/Button'
import { Label } from '../primitives/Label'
import { Input } from '../primitives/Input'

export interface CreateOriginDestinyFormData {
  name: string
  countryId?: number
}

interface CreateOriginDestinyDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  onSave: (_data: CreateOriginDestinyFormData) => void
  onCancel: () => void
  isPending?: boolean
  title?: string
  inputLabel?: string
  inputPlaceholder?: string
}

/**
 * CreateOriginDestinyDialog - Modal genérico para crear origen o destino
 *
 * Modal simple con un campo de texto para ingresar el nombre
 * de un nuevo origen o destino
 *
 * @example
 * ```tsx
 * <CreateOriginDestinyDialog
 *   open={showDialog}
 *   onOpenChange={setShowDialog}
 *   onSave={handleCreateOrigin}
 *   onCancel={() => setShowDialog(false)}
 *   title="Crear Origen"
 *   inputLabel="Nombre"
 * />
 * ```
 */
export function CreateOriginDestinyDialog({
  open,
  onOpenChange,
  onSave,
  onCancel,
  isPending = false,
  title = 'Crear Origen',
  inputLabel = 'Nombre',
  inputPlaceholder = 'Ingrese el nombre',
}: CreateOriginDestinyDialogProps) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateOriginDestinyFormData>({
    defaultValues: {
      name: '',
      countryId: undefined,
    },
  })

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open, reset])

  const onSubmit = (data: CreateOriginDestinyFormData) => {
    onSave(data)
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="w-full max-w-[400px] bg-neutral-50 p-0 shadow-lg">
        {/* Dialog Header */}
        <DialogHeader className="border-b border-neutral-300 px-4 py-4">
          <DialogTitle className="text-xl font-semibold text-neutral-900">
            {title}
          </DialogTitle>
        </DialogHeader>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Info Section */}
          <div className="flex flex-col gap-4 bg-neutral-50 px-4 py-4">
            {/* Country Field */}
            <div>
              <FormSearchableSelect<CreateOriginDestinyFormData>
                className="w-full"
                control={control}
                defaultValue={undefined}
                emptyMessage="No se encontraron resultados"
                fetcher={async (search: string) => {
                  const params = search ? { search } : undefined
                  const items = await getCountries(params)
                  return items.map((c) => ({ id: c.id, label: c.name }))
                }}
                label="País"
                name="countryId"
                placeholder="Seleccionar país"
                rules={{ required: 'El país es requerido' }}
                searchPlaceholder="Buscar país..."
                showLabel
              />
            </div>

            {/* Nombre Field */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="name" required>
                {inputLabel}
              </Label>
              <Input
                autoComplete="off"
                error={!!errors.name}
                id="name"
                placeholder={inputPlaceholder}
                {...register('name', {
                  required: 'Este campo es requerido',
                  minLength: {
                    value: 2,
                    message: 'Debe tener al menos 2 caracteres',
                  },
                })}
              />
              {errors.name && (
                <span className="text-xs text-red-600">
                  {errors.name.message}
                </span>
              )}
            </div>
          </div>

          {/* Dialog Footer */}
          <DialogFooter className="border-t border-neutral-300 px-4 py-4">
            <div className="flex w-full items-center justify-between gap-2">
              <Button
                disabled={isPending}
                onClick={onCancel}
                size="md"
                type="button"
                variant="outline"
              >
                <span>Cancelar</span>
              </Button>
              <Button
                className="bg-slate-800 hover:bg-slate-700"
                disabled={isPending}
                size="md"
                type="submit"
              >
                <span>Crear</span>
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
